import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Calculator, TrendingUp } from 'lucide-react';
import axios from 'axios';

function EstimationModal({ 
  isOpen, 
  onClose, 
  productInfo,
  onValidate
}) {
  const [quantiteTotale, setQuantiteTotale] = useState(productInfo?.quantite_detectee || 1);
  const [unite, setUnite] = useState(productInfo?.unite_detectee || 'pièces');
  const [consommationJour, setConsommationJour] = useState(0);

  useEffect(() => {
    if (isOpen && productInfo) {
      setQuantiteTotale(productInfo.quantite_detectee || 1);
      setUnite(productInfo.unite_detectee || 'pièces');
      
      // Essayer d'obtenir l'estimation depuis l'apprentissage IA
      fetchEstimationIA();
    }
  }, [isOpen, productInfo]);

  const fetchEstimationIA = async () => {
    try {
      // Appeler le backend pour obtenir l'estimation apprise
      const response = await axios.post('/items/estimation-ia', {
        categorie: productInfo.categorie,
        unite: productInfo.unite_detectee || 'pièces'
      });
      
      if (response.data.success && response.data.consommation_apprise) {
        // Utiliser l'estimation apprise
        setConsommationJour(response.data.consommation_apprise);
      } else {
        // Fallback: calculer estimation automatique
        const estimation = calculerEstimationAuto(
          productInfo.categorie, 
          productInfo.unite_detectee || 'pièces',
          productInfo.quantite_detectee || 1
        );
        setConsommationJour(estimation);
      }
    } catch (error) {
      // En cas d'erreur, utiliser l'estimation par défaut
      const estimation = calculerEstimationAuto(
        productInfo.categorie, 
        productInfo.unite_detectee || 'pièces',
        productInfo.quantite_detectee || 1
      );
      setConsommationJour(estimation);
    }
  };

  const calculerEstimationAuto = (categorie, unite, quantite) => {
    const cat = (categorie || '').toLowerCase();
    
    // Estimations par catégorie et unité
    if (cat.includes('eau') || cat.includes('boisson')) {
      if (unite === 'L' || unite === 'l') return 0.5; // 500mL/jour
      if (unite === 'mL' || unite === 'ml') return 500;
      return 1; // 1 bouteille/jour
    }
    
    if (cat.includes('lait')) {
      if (unite === 'L' || unite === 'l') return 0.25;
      if (unite === 'mL' || unite === 'ml') return 250;
      return 1;
    }
    
    if (cat.includes('pain')) {
      return 0.5; // 500g/jour ou 0.5 pain/jour
    }
    
    if (cat.includes('pâte') || cat.includes('tartiner')) {
      if (unite === 'g') return 30;
      return 0.05; // 5% par jour
    }
    
    if (cat.includes('yaourt') || cat.includes('fromage')) {
      if (unite === 'g') return 100;
      return 1; // 1 pot/jour
    }
    
    // Estimation par défaut : 5% de la quantité totale par jour
    if (unite === 'g' || unite === 'mL' || unite === 'ml') {
      return Math.max(10, quantite * 0.05);
    }
    
    return Math.max(0.1, quantite * 0.1); // 10% par jour pour les pièces
  };

  const calculerDureeEstimee = () => {
    if (consommationJour <= 0) return '∞';
    const jours = Math.floor(quantiteTotale / consommationJour);
    if (jours < 1) return '< 1 jour';
    if (jours === 1) return '1 jour';
    if (jours < 7) return `${jours} jours`;
    const semaines = Math.floor(jours / 7);
    return `${semaines} semaine${semaines > 1 ? 's' : ''} (${jours}j)`;
  };

  const handleValider = () => {
    onValidate({
      quantite_totale: parseFloat(quantiteTotale),
      unite: unite,
      consommation_jour: parseFloat(consommationJour),
      duree_estimee_jours: Math.floor(quantiteTotale / consommationJour)
    });
    onClose();
  };

  const handlePasser = () => {
    // Utiliser l'estimation automatique
    onValidate({
      quantite_totale: parseFloat(quantiteTotale),
      unite: unite,
      consommation_jour: parseFloat(consommationJour),
      duree_estimee_jours: Math.floor(quantiteTotale / consommationJour),
      estimation_auto: true
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="bg-gradient-to-r from-purple-50 to-blue-50">
          <CardTitle className="flex items-center space-x-2">
            <Calculator className="w-5 h-5 text-purple-600" />
            <span>Estimation de Consommation</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-4">
          <div className="bg-blue-50 p-3 rounded-lg">
            <p className="text-sm font-medium text-blue-900">
              📦 {productInfo?.nom || 'Produit'}
            </p>
            {productInfo?.marque && (
              <p className="text-xs text-blue-700 mt-1">
                {productInfo.marque}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Quantité totale</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={quantiteTotale}
                onChange={(e) => setQuantiteTotale(parseFloat(e.target.value) || 0)}
              />
            </div>
            <div className="space-y-2">
              <Label>Unité</Label>
              <Select value={unite} onValueChange={setUnite}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pièces">Pièce(s)</SelectItem>
                  <SelectItem value="g">Grammes (g)</SelectItem>
                  <SelectItem value="kg">Kilogrammes (kg)</SelectItem>
                  <SelectItem value="mL">Millilitres (mL)</SelectItem>
                  <SelectItem value="L">Litres (L)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="flex items-center space-x-2">
              <TrendingUp size={14} className="text-orange-600" />
              <span>Consommation par jour</span>
            </Label>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={consommationJour}
              onChange={(e) => setConsommationJour(parseFloat(e.target.value) || 0)}
              placeholder="Ex: 0.5"
            />
            <p className="text-xs text-gray-500">
              Estimation automatique basée sur la catégorie. Vous pouvez modifier.
            </p>
          </div>

          {consommationJour > 0 && (
            <div className="bg-green-50 p-3 rounded-lg border border-green-200">
              <p className="text-sm text-green-800">
                ⏱️ <strong>Durée estimée:</strong> {calculerDureeEstimee()}
              </p>
              <p className="text-xs text-green-700 mt-1">
                Avec {consommationJour} {unite}/jour, ce stock durera environ {calculerDureeEstimee()}
              </p>
            </div>
          )}

          <div className="flex space-x-2 pt-2">
            <Button
              onClick={handleValider}
              className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
            >
              ✓ Valider l'estimation
            </Button>
            <Button
              onClick={handlePasser}
              variant="outline"
              className="flex-1"
            >
              Passer (auto)
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default EstimationModal;
